/**
 * Single source of truth for Companion's global keyboard shortcuts.
 *
 * Both the key handler (useCompanionShortcuts) and every piece of UI that
 * displays a shortcut (the cheat-sheet modal, the user-menu hints, the pending-G
 * hint) read from here, so a binding and its label can never drift apart.
 */

/** How long the `G` prefix stays armed while waiting for the second key. */
export const GO_SEQUENCE_TIMEOUT_MS = 1200;

/**
 * How close together the two Escapes must fall to count as an Esc-Esc (switch
 * user). A lone Escape with nothing open, or a second one after this window,
 * does nothing.
 */
export const DOUBLE_ESCAPE_TIMEOUT_MS = 500;

/**
 * After an Escape dismisses an overlay, a lone Escape is ignored for this long
 * before it can start an Esc-Esc. Rapidly mashing Escape to close things (a
 * stack of overlays) then can't chain into an accidental sign-out, which would
 * cost a full re-auth.
 */
export const OVERLAY_DISMISS_COOLDOWN_MS = 300;

/** Display tokens for the switch-user shortcut (Esc pressed twice). */
export const SWITCH_USER_TOKENS: string[] = ["Esc", "Esc"];

/** The Mac ⌘ vs. everywhere-else Ctrl split, used for the Search hint. */
export const isMacPlatform = (): boolean =>
  typeof navigator !== "undefined" &&
  /mac/i.test(navigator.userAgent ?? navigator.platform ?? "");

export type GoToTarget = "home" | "calendar" | "tasks" | "admin";

export interface GoToShortcut {
  /** The second key pressed after `G`, always lowercase. */
  key: string;
  target: GoToTarget;
  label: string;
  /** Display tokens for the cheat sheet / hints, e.g. `["G", "H"]`. */
  tokens: string[];
  /** Only shown (and acted on) for users who pass `canAccessAdmin`. */
  adminOnly?: boolean;
}

export const GO_TO_SHORTCUTS: GoToShortcut[] = [
  { key: "h", target: "home", label: "Home", tokens: ["G", "H"] },
  { key: "c", target: "calendar", label: "Calendar", tokens: ["G", "C"] },
  { key: "t", target: "tasks", label: "Tasks", tokens: ["G", "T"] },
  {
    key: "a",
    target: "admin",
    label: "Admin",
    tokens: ["G", "A"],
    adminOnly: true,
  },
];

/** Display tokens for the Search shortcut, platform-aware. */
export const searchShortcutTokens = (): string[] =>
  isMacPlatform() ? ["⌘", "K"] : ["Ctrl", "K"];

/** Look up the target bound to the given second key (after `G`). */
export const goToTargetForKey = (key: string): GoToTarget | undefined =>
  GO_TO_SHORTCUTS.find((s) => s.key === key.toLowerCase())?.target;

/** The keys bound to a single go-to target, e.g. `["G", "H"]`. */
export const goToTokensFor = (target: GoToTarget): string[] =>
  GO_TO_SHORTCUTS.find((s) => s.target === target)?.tokens ?? [];

export interface ShortcutRow {
  tokens: string[];
  label: string;
}

/** Rows for the cheat sheet's "General" group. */
export const generalShortcutRows = (): ShortcutRow[] => [
  { tokens: searchShortcutTokens(), label: "Search" },
  { tokens: ["?"], label: "Keyboard shortcuts" },
  { tokens: ["Esc"], label: "Close menus and dialogs" },
  { tokens: SWITCH_USER_TOKENS, label: "Switch user" },
];

/** Rows for the cheat sheet's "Go to" group; admin-only rows require access. */
export const goToShortcutRows = (hasAdminAccess: boolean): ShortcutRow[] =>
  GO_TO_SHORTCUTS.filter((s) => !s.adminOnly || hasAdminAccess).map(
    ({ tokens, label }) => ({ tokens, label }),
  );
